import { useLocation, Link } from 'react-router-dom'
import { CheckCircle2, ArrowRight, Download } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

export default function OrderSuccess() {
  const location = useLocation()
  const { reference, amount } = location.state || {}

  if (!reference) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-display font-bold text-ink mb-4">
            No order found
          </h2>
          <Link to="/products">
            <Button variant="primary">Browse Products</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <SEO title="Order Confirmed" description="" path="/order-success" noindex />
      <div className="max-w-2xl w-full">
        <div className="rounded border border-theme bg-surface p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', border: '2px solid color-mix(in srgb, var(--accent) 30%, transparent)' }}>
            <CheckCircle2 size={40} className="text-accent" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl md:text-4xl font-display font-bold text-ink mb-4">
            Payment Successful!
          </h1>
          <p className="text-lg text-[var(--ink-soft)] mb-8">
            Your order has been received and payment confirmed.
          </p>

          {/* Order Details */}
          <div className="rounded bg-sunk p-6 mb-8 text-left">
            <h3 className="font-bold text-ink mb-4">Order Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)]">Reference Number</span>
                <span className="font-boxy font-medium text-ink">{reference}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)]">Amount Paid</span>
                <span className="font-bold text-accent">
                  GHS {amount?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)]">Status</span>
                <span className="px-2 py-1 rounded-full text-xs font-semibold text-accent" style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}>
                  Confirmed
                </span>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="rounded p-6 mb-8 text-left" style={{ background: 'color-mix(in srgb, var(--accent) 5%, transparent)', borderLeft: '4px solid var(--accent)' }}>
            <h3 className="font-bold text-ink mb-4">What happens next?</h3>
            <div className="space-y-3">
              {[
                'You will receive an email confirmation within 5 minutes',
                'Our team will contact you within 24 hours to arrange delivery',
                'We will deliver the equipment to your school',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 text-white" style={{ background: 'var(--accent)' }}>
                    {i + 1}
                  </span>
                  <p className="text-sm text-[var(--ink-soft)]">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="ghost" size="lg">
              <Download size={16} /> Download Receipt
            </Button>
            <Link to="/products">
              <Button variant="primary" size="lg">
                Browse More Products <ArrowRight size={16} />
              </Button>
            </Link>
          </div>

          {/* Support */}
          <p className="text-xs text-[var(--ink-soft)] mt-8">
            Questions about your order? Contact us at{' '}
            <a href="mailto:info@myschoolportal.xyz" className="text-accent">
              info@myschoolportal.xyz
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
